import React, { useState, useEffect } from 'react';
import { Users, Crown, Trash2, Mail, Clock, Loader2 } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import Modal from '../../common/modals/Modal';
import { useAit } from '../../../context/AitContext';
import toast from 'react-hot-toast';

const AitPropertyMembersModal = ({ isOpen, onClose, propertyId, isOwner, currentUserRole, onInviteClick }) => {
  const { theme } = useTheme();
  const { fetchPropertyMembers, fetchPropertyInvitations, removePropertyMember, revokePropertyInvitation } = useAit();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('members');

  useEffect(() => {
    if (isOpen && propertyId) {
      loadMembers();
      loadInvitations();
    }
  }, [isOpen, propertyId]);

  const loadMembers = async () => {
    setLoading(true);
    const result = await fetchPropertyMembers(propertyId);
    if (result.success) setMembers(result.members);
    setLoading(false);
  };

  const loadInvitations = async () => {
    const result = await fetchPropertyInvitations(propertyId);
    if (result.success) setInvitations(result.invitations);
  };

  const handleRemoveMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this member?')) return;
    const result = await removePropertyMember(propertyId, memberId);
    if (result.success) {
      toast.success('Member removed');
      loadMembers();
    } else {
      toast.error(result.error || 'Failed to remove member');
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    if (!confirm('Are you sure you want to revoke this invitation?')) return;
    const result = await revokePropertyInvitation(propertyId, invitationId);
    if (result.success) {
      toast.success('Invitation revoked');
      loadInvitations();
    } else {
      toast.error(result.error || 'Failed to revoke invitation');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
      editor: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      viewer: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    };
    return colors[role] || colors.viewer;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const canManage = isOwner || currentUserRole === 'editor';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Property Members" size="lg">
      {/* Tabs */}
      <div className={`flex gap-4 mb-4 border-b ${
        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <button
          onClick={() => setActiveTab('members')}
          className={`pb-2 px-1 text-sm transition-colors ${
            activeTab === 'members'
              ? theme === 'dark'
                ? 'border-b-2 border-blue-500 text-blue-400'
                : 'border-b-2 border-blue-500 text-blue-600'
              : theme === 'dark'
              ? 'text-gray-400 hover:text-gray-300'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Users className="w-3.5 h-3.5 inline mr-1.5" />
          Members ({members.length})
        </button>
        <button
          onClick={() => setActiveTab('invitations')}
          className={`pb-2 px-1 text-sm transition-colors ${
            activeTab === 'invitations'
              ? theme === 'dark'
                ? 'border-b-2 border-blue-500 text-blue-400'
                : 'border-b-2 border-blue-500 text-blue-600'
              : theme === 'dark'
              ? 'text-gray-400 hover:text-gray-300'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Mail className="w-3.5 h-3.5 inline mr-1.5" />
          Pending Invites ({invitations.length})
        </button>
      </div>

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : members.length === 0 ? (
            <p className={`text-center py-8 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              No members yet
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {members.map((member) => (
                <div
                  key={member.id || member.user.id}
                  className={`flex items-center justify-between p-2.5 rounded-lg border ${
                    theme === 'dark'
                      ? 'border-gray-700 bg-gray-750'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                      theme === 'dark' ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {member.user.username.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className={`font-medium text-sm truncate ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {member.user.username}
                        </p>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${
                          getRoleBadgeColor(member.role)
                        }`}>
                          {member.role}
                        </span>
                        {member.is_active && (
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0" title="Active now" />
                        )}
                      </div>
                      <p className={`text-xs truncate ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {member.user.email}
                      </p>
                      {member.joined_at && (
                        <p className={`text-xs ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          Joined {formatDate(member.joined_at)}
                        </p>
                      )}
                    </div>
                  </div>

                  {isOwner && member.role !== 'owner' && !member.is_property_owner && (
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                        theme === 'dark'
                          ? 'text-red-400 hover:bg-red-900/20'
                          : 'text-red-500 hover:bg-red-50'
                      }`}
                      title="Remove member"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {canManage && (
            <div className="flex justify-center mt-4">
              <button
                onClick={() => {
                  onClose();
                  onInviteClick?.();
                }}
                className="px-6 py-2 rounded-lg font-semibold bg-blue-500 hover:bg-blue-600 text-white transition-colors text-sm"
              >
                Invite New Member
              </button>
            </div>
          )}
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div>
          {invitations.length === 0 ? (
            <div className="text-center py-8">
              <Mail className={`w-12 h-12 mx-auto mb-3 ${
                theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
              }`} />
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                No pending invitations
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className={`flex items-center justify-between p-2.5 rounded-lg border ${
                    theme === 'dark'
                      ? 'border-gray-700 bg-gray-750'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <p className={`font-medium text-sm truncate ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {invitation.email}
                      </p>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${
                        getRoleBadgeColor(invitation.role)
                      }`}>
                        {invitation.role}
                      </span>
                    </div>
                    <p className={`text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      <Clock className="w-3 h-3 inline mr-1" />
                      Invited by {invitation.invited_by.username}
                    </p>
                    <p className={`text-xs ${
                      theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      Expires {formatDate(invitation.expires_at)}
                    </p>
                  </div>

                  {canManage && (
                    <button
                      onClick={() => handleRevokeInvitation(invitation.id)}
                      className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                        theme === 'dark'
                          ? 'text-red-400 hover:bg-red-900/20'
                          : 'text-red-500 hover:bg-red-50'
                      }`}
                      title="Revoke invitation"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default AitPropertyMembersModal;
